import React, { useEffect, useState } from 'react';
import { Input, Menu, Layout, Select, Form, Typography, Row, Col, Slider, Switch } from 'antd';
import './HeaderComponent.css';
import { usePermissions } from './PermissionsProvider';
import { ConfigService } from './services/ConfigService';
import RequestTimeline from './RequestTimeline';
import MetaLogo from "./assets/meta_primary.svg";

// Define an interface for the component props
interface CompletionConfigComponentProps {
    meta: any
}

// Map of model names to display strings
const modelNameMap: Record<string, string> = {
    'maitai:red_robin': 'Maitai Groq LoRA',
    'maitai:predibase-llama-3.1-8b-instruct': 'Vanilla Llama 3.1 8b'
};

const CompletionConfigComponent: React.FC<CompletionConfigComponentProps> = ({ meta }) => {
    const { apiKey } = usePermissions()
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        if (apiKey) {
        }
    }, [apiKey])

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Responsive styles that will be reused
    const containerStyle = {
        width: '100%', 
        display: 'flex', 
        flexDirection: 'column' as const, 
        alignItems: 'start' as const, 
        background: 'rgba(0,0,0,0.02)', 
        borderRadius: '10px', 
        padding: isMobile ? '12px' : '20px 20px', 
        gap: '20px',
        boxSizing: 'border-box' as const,
        marginBottom: '16px'
    };

    const labelStyle = {
        fontSize: isMobile ? '13px' : '14px',
        fontWeight: '600',
        color: 'rgba(0,0,0,0.8)',
        textAlign: 'start' as const
    };

    const valueStyle = {
        fontSize: isMobile ? '13px' : '14px',
        fontWeight: '400',
        color: 'rgba(0,0,0,0.8)',
        textAlign: 'end' as const
    };

    // Helper function to display models as a comma-separated string
    const displayModels = (models: string | string[] | undefined): React.ReactNode => {
        if (!models) return "";
        
        const formatModel = (model: string) => {
            // Use the model display name if available
            const displayName = modelNameMap[model] || model;
            
            if (model.toLowerCase().includes('llama') || model.toLowerCase().includes('red_robin')) {
                return (
                    <span key={model} style={{ display: 'inline-block', textAlign: 'right' }}>
                        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <span>{displayName}</span>
                            <img src={MetaLogo} alt="Meta" style={{ height: '20px', marginTop: '2px' }} />
                        </span>
                    </span>
                );
            }
            return displayName;
        };

        if (typeof models === "string") {
            return formatModel(models);
        }

        return (
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                {models.map((model, index) => (
                    <React.Fragment key={model}>
                        {formatModel(model)}
                    </React.Fragment>
                ))}
            </span>
        );
    };

    return (
        <div style={{
            width: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'start', 
            gap: '12px',
            maxWidth: '100%',
            boxSizing: 'border-box'
        }}>
            <div style={containerStyle}>
                {(meta?.chat_completion_request?.params?.models || meta?.chat_completion_request?.params?.model || meta?.models || meta?.model) && (
                    <Row style={{width: '100%'}}>
                        <Col span={isMobile ? 12 : 10}>
                            <Typography style={labelStyle}>Model</Typography>
                        </Col>
                        <Col span={isMobile ? 12 : 14}>
                            <Typography style={valueStyle}>
                                {displayModels(
                                    meta?.chat_completion_request?.params?.models || 
                                    meta?.chat_completion_request?.params?.model || 
                                    meta?.models || 
                                    meta?.model
                                )}
                            </Typography>
                        </Col>
                    </Row>
                )}
                {(meta?.chat_completion_request?.params?.temperature || meta?.temperature) && (
                    <Row style={{width: '100%'}}>
                        <Col span={isMobile ? 12 : 10}>
                            <Typography style={labelStyle}>Temperature</Typography>
                        </Col>
                        <Col span={isMobile ? 12 : 14}>
                            <Typography style={valueStyle}>{meta?.chat_completion_request?.params?.temperature || meta?.temperature}</Typography>
                        </Col>
                    </Row>
                )}
                {meta?.chat_completion_request?.evaluation_enabled !== undefined && (
                    <Row style={{width: '100%'}}>
                        <Col span={isMobile ? 12 : 10}>
                            <Typography style={labelStyle}>Evaluations</Typography>
                        </Col>
                        <Col span={isMobile ? 12 : 14} style={{display: 'flex', justifyContent: 'end'}}>
                            <Typography style={valueStyle}>{meta?.chat_completion_request?.evaluation_enabled ? 'Yes' : 'No'}</Typography>
                        </Col>
                    </Row>
                )}
                {meta?.chat_completion_request?.apply_corrections !== undefined && (
                    <Row style={{width: '100%'}}>
                        <Col span={isMobile ? 12 : 10}>
                            <Typography style={labelStyle}>Autocorrect</Typography>
                        </Col>
                        <Col span={isMobile ? 12 : 14} style={{display: 'flex', justifyContent: 'end'}}>
                            <Typography style={valueStyle}>{meta?.chat_completion_request?.apply_corrections ? 'Yes' : 'No'}</Typography>
                        </Col>
                    </Row>
                )}
                {meta?.inf_time && (
                    <Row style={{width: '100%'}}>
                        <Col span={isMobile ? 12 : 10}>
                            <Typography style={labelStyle}>Inference Time</Typography>
                        </Col>
                        <Col span={isMobile ? 12 : 14} style={{display: 'flex', justifyContent: 'end'}}>
                            <Typography style={valueStyle}>{meta?.inf_time}ms</Typography>
                        </Col>
                    </Row>
                )}
                {meta?.ttft !== undefined && (
                    <Row style={{width: '100%'}}>
                        <Col span={isMobile ? 12 : 10}>
                            <Typography style={labelStyle}>TTFT</Typography>
                        </Col>
                        <Col span={isMobile ? 12 : 14} style={{display: 'flex', justifyContent: 'end'}}>
                            <Typography style={valueStyle}>{meta?.ttft}ms</Typography>
                        </Col>
                    </Row>
                )}
                {meta?.tps !== undefined && (
                    <Row style={{width: '100%'}}>
                        <Col span={isMobile ? 12 : 10}>
                            <Typography style={labelStyle}>Tokens/sec</Typography>
                        </Col>
                        <Col span={isMobile ? 12 : 14} style={{display: 'flex', justifyContent: 'end'}}>
                            <Typography style={valueStyle}>{meta?.tps}</Typography>
                        </Col>
                    </Row>
                )}
                {(meta?.input_tokens !== undefined || meta?.output_tokens !== undefined) && (
                    <Row style={{width: '100%'}}>
                        <Col span={isMobile ? 12 : 10}>
                            <Typography style={labelStyle}>Tokens</Typography>
                        </Col>
                        <Col span={isMobile ? 12 : 14} style={{display: 'flex', justifyContent: 'end'}}>
                            <Typography style={valueStyle}>{meta?.input_tokens || 0} {'>'}  {meta?.output_tokens || 0}</Typography>
                        </Col>
                    </Row>
                )}
            </div>

            {meta?.chat_completion_request?.context_query && (
                <div style={containerStyle}>
                    <Row style={{width: '100%'}} gutter={[8, 8]}>
                        <Col span={24}>
                            <Typography style={labelStyle}>Context Query</Typography>
                        </Col>
                        <Col span={24}>
                            <Input 
                                style={{
                                    width: '100%',
                                    fontSize: isMobile ? '12px' : '14px'
                                }} 
                                value={meta?.chat_completion_request?.context_query} 
                            />
                        </Col>
                    </Row>
                    <Row style={{width: '100%'}} gutter={[8, 8]}>
                        <Col span={24}>
                            <Typography style={labelStyle}>Context</Typography>
                        </Col>
                        <Col span={24}>
                            <Input.TextArea 
                                style={{
                                    width: '100%',
                                    fontSize: isMobile ? '12px' : '14px'
                                }} 
                                rows={isMobile ? 6 : 12} 
                                value={meta?.chat_completion_request?.context} 
                            />
                        </Col>
                    </Row>
                </div>
            )}

            {meta && meta.client_start_time && (
                <div style={{
                    ...containerStyle,
                    padding: 0,
                    overflow: 'hidden'
                }}>
                    <RequestTimeline
                        client_start_time={meta.client_start_time}
                        server_start_time={meta.server_start_time}
                        request_start_time={meta.request_start_time}
                        first_token_time={meta.first_token_time}
                        request_end_time={meta.request_end_time}
                        server_end_time={meta.server_end_time}
                        client_end_time={meta.client_end_time}
                    />
                </div>
            )}
        </div>
    );
}

export default CompletionConfigComponent;
